/**
 * TYPES
 */
import {ICustomerContactAlreadyExists} from 'src/aggregates/customers/events.d';
import {ICustomerContactTypeAdded} from 'src/aggregates/customers/events.d';
import {ICustomerContactTypesFetched} from 'src/aggregates/customers/events.d';
import {ICustomerContactTypesFetching} from 'src/aggregates/customers/events.d';
import {ICustomerContactTypesNotFetched}
    from 'src/aggregates/customers/events.d';
import * as Events from 'src/aggregates/customers/events.d';
import {types} from 'src/aggregates/customers/events.d';
import {IContact} from 'src/aggregates/customers/state.d';
import {ICustomer} from 'src/aggregates/customers/state.d';
import {ICustomerAgent} from 'src/aggregates/customers/state.d';
import {ICustomerTag} from 'src/aggregates/customers/state.d';
import {_aggregates} from 'src/aggregates/index.d';
import {IEventCreator} from 'src/aggregates/index.d';


/**
 * CODE
 */

/**
 * I create a customer added event.
 *
 * :param id: id of the customer
 * :param agents: priority agents of the customer
 * :param company: company of the customer
 * :param contacts: contacts of the customer
 * :param name: name of the customer
 * :param unassociated: if customer was created based on a unassociated contact
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param city: city of the customer
 * :param document: document of the customer
 * :param email: email of the customer
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param tags: customer tags
 * :param zipcode: zipcode of the customer
 *
 * :returns: customer added event
 */
const customerAdded: IEventCreator<Events.ICustomerAdded> =
    (
        id: number,
        agents: ICustomerAgent[],
        company: number,
        contacts: IContact[],
        name: string,
        unassociated: boolean,
        address1?: string,
        address2?: string,
        city?: string,
        code?: string,
        description?: string,
        document?: string,
        email?: string,
        organization?: string,
        state?: string,
        tags?: ICustomerTag[],
        zipcode?: string,
    ): Events.ICustomerAdded => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_ADDED,
        _target: id,
        address1,
        address2,
        agents,
        city,
        code,
        company,
        contacts,
        description,
        document,
        email,
        name,
        organization,
        state,
        tags,
        type: types.CUSTOMER_ADDED,
        unassociated,
        zipcode,
    });


/**
 * I create a customer all pages loaded event.
 *
 * :returns: customer all pages loaded event
 */
function customerAllPagesLoaded (): Events.ICustomerAllPagesLoaded
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_ALL_PAGES_LOADED,
        _target: null,
        type: types.CUSTOMER_ALL_PAGES_LOADED,
    };
}


/**
 * I create a customer contact already exists event.
 *
 * :returns: customer contact already exists event
 */
function customerContactAlreadyExists (): ICustomerContactAlreadyExists
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_CONTACT_ALREADY_EXISTS,
        _target: null,
        type: types.CUSTOMER_CONTACT_ALREADY_EXISTS,
    };
}


/**
 * I create a customer contact type added event.
 *
 * :param channel: contact type channel
 * :param id: contact type id
 * :param name: contact type name
 * :param prefix: contact type prefix
 *
 * :returns: customer contact type added event
 */
function customerContactTypeAdded (
    channel: string,
    id: number,
    name: string,
    prefix: string,
): ICustomerContactTypeAdded
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_CONTACT_TYPE_ADDED,
        _target: null,
        channel,
        id,
        name,
        prefix,
        type: types.CUSTOMER_CONTACT_TYPE_ADDED,
    };
}


/**
 * I create a customer contact types fetched event.
 *
 * :returns: customer contact types fetched event
 */
function customerContactTypesFetched (): ICustomerContactTypesFetched
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_CONTACT_TYPES_FETCHED,
        _target: null,
        type: types.CUSTOMER_CONTACT_TYPES_FETCHED,
    };
}


/**
 * I create a customer contact types fetching event.
 *
 * :returns: customer contact types fetching event
 */
function customerContactTypesFetching (): ICustomerContactTypesFetching
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_CONTACT_TYPES_FETCHING,
        _target: null,
        type: types.CUSTOMER_CONTACT_TYPES_FETCHING,
    };
}


/**
 * I create a customer contact types not fetched event.
 *
 * :returns: customer contact types not fetched event
 */
function customerContactTypesNotFetched (): ICustomerContactTypesNotFetched
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_CONTACT_TYPES_NOT_FETCHED,
        _target: null,
        type: types.CUSTOMER_CONTACT_TYPES_NOT_FETCHED,
    };
}


/**
 * I create a customer created event.
 *
 * :param id: customer id
 *
 * :returns: customer created event
 */
const customerCreated: IEventCreator<Events.ICustomerCreated> =
    (id: number): Events.ICustomerCreated => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_CREATED,
        _target: id,
        type: types.CUSTOMER_CREATED,
    });


/**
 * I create a customer creating event.
 *
 * :returns: customer creating event
 */
const customerCreating: IEventCreator<Events.ICustomerCreating> =
    (): Events.ICustomerCreating => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_CREATING,
        _target: null,
        type: types.CUSTOMER_CREATING,
    });


/**
 * I create a customer focused event.
 *
 * :param customer: customer id or path string or nothing
 *
 * :returns: customer focused event
 */
function customerFocused (
    customer: string | number | null,
): Events.ICustomerFocused
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_FOCUSED,
        _target: null,
        customer,
        type: types.CUSTOMER_FOCUSED,
    };
}


/**
 * I create a customer loading event.
 *
 * :returns: customer loading event
 */
const customerLoading: IEventCreator<Events.ICustomerLoading> =
    (): Events.ICustomerLoading => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_LOADING,
        _target: null,
        type: types.CUSTOMER_LOADING,
    });


/**
 * I create a customer not created event.
 *
 * :returns: customer not created event
 */
const customerNotCreated: IEventCreator<Events.ICustomerNotCreated> =
    (): Events.ICustomerNotCreated => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_NOT_CREATED,
        _target: null,
        type: types.CUSTOMER_NOT_CREATED,
    });


/**
 * I create a customer not loaded event.
 *
 * :returns: customer not loaded event
 */
const customerNotLoaded: IEventCreator<Events.ICustomerNotLoaded> =
    (): Events.ICustomerNotLoaded => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_NOT_LOADED,
        _target: null,
        type: types.CUSTOMER_NOT_LOADED,
    });


/**
 * I create a customer not removed event.
 *
 * :param id: customer id
 *
 * :returns: customer not removed event
 */
const customerNotRemoved: IEventCreator<Events.ICustomerNotRemoved> =
    (id: number): Events.ICustomerNotRemoved => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_NOT_REMOVED,
        _target: id,
        type: types.CUSTOMER_NOT_REMOVED,
    });


/**
 * I create a customer not tagged event.
 *
 * :param id: customer id
 *
 * :returns: customer not tagged event
 */
function customerNotTagged (id: number): Events.ICustomerNotTagged
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_NOT_TAGGED,
        _target: id,
        type: types.CUSTOMER_NOT_TAGGED,
    };
}


/**
 * I create a customer not untagged event.
 *
 * :param id: customer id
 *
 * :returns: customer not untagged event
 */
function customerNotUntagged (id: number): Events.ICustomerNotUntagged
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_NOT_UNTAGGED,
        _target: id,
        type: types.CUSTOMER_NOT_UNTAGGED,
    };
}


/**
 * I create a customer removed event.
 *
 * :param id: customer id
 *
 * :returns: customer removed event
 */
const customerRemoved: IEventCreator<Events.ICustomerRemoved> =
    (id: number): Events.ICustomerRemoved => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_REMOVED,
        _target: id,
        type: types.CUSTOMER_REMOVED,
    });


/**
 * I create a customer removing event.
 *
 * :param id: customer id
 *
 * :returns: customer removing event
 */
const customerRemoving: IEventCreator<Events.ICustomerRemoving> =
    (id: number): Events.ICustomerRemoving => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_REMOVING,
        _target: id,
        type: types.CUSTOMER_REMOVING,
    });


/**
 * I create a customer search term added event.
 *
 * :param term: search term
 *
 * :returns: customer search term added event
 */
function customerSearchTermAdded (term: string): Events.ICustomerSearchTermAdded
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_SEARCH_TERM_ADDED,
        _target: null,
        term,
        type: types.CUSTOMER_SEARCH_TERM_ADDED,
    };
}


/**
 * I create a customer siblings dropped event.
 *
 * :param id: contact id
 *
 * :returns: customer siblings dropped event
 */
function customerSiblingsDropped (
    id: IContact['id'],
): Events.ICustomerSiblingsDropped
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_SIBLINGS_DROPPED,
        _target: id,
        type: types.CUSTOMER_SIBLINGS_DROPPED,
    };
}


/**
 * I create a customer siblings loaded event.
 *
 * :param id: contact id
 * :param siblings: uids the contact is also known by
 *
 * :returns: customer siblings loaded event
 */
function customerSiblingsLoaded (
    id: IContact['id'],
    siblings: string[],
): Events.ICustomerSiblingsLoaded
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_SIBLINGS_LOADED,
        _target: id,
        siblings,
        type: types.CUSTOMER_SIBLINGS_LOADED,
    };
}


/**
 * I create a customer siblings loading event.
 *
 * :param id: contact id
 *
 * :returns: customer siblings loading event
 */
function customerSiblingsLoading (
    id: IContact['id'],
): Events.ICustomerSiblingsLoading
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_SIBLINGS_LOADING,
        _target: id,
        type: types.CUSTOMER_SIBLINGS_LOADING,
    };
}


/**
 * I create a customer tagged event.
 *
 * :param id: customer id
 *
 * :returns: customer tagged event
 */
function customerTagged (
    id: number,
    customerTag: ICustomerTag,
): Events.ICustomerTagged
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_TAGGED,
        _target: id,
        customerTag,
        type: types.CUSTOMER_TAGGED,
    };
}


/**
 * I create a customer tagging event.
 *
 * :param id: customer id
 *
 * :returns: customer tagging event
 */
function customerTagging (id: number): Events.ICustomerTagging
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_TAGGING,
        _target: id,
        type: types.CUSTOMER_TAGGING,
    };
}


/**
 * I create a customer not updated event.
 *
 * :returns: customer not updated event
 */
const customerNotUpdated: IEventCreator<Events.ICustomerNotUpdated> =
    (): Events.ICustomerNotUpdated => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_NOT_UPDATED,
        _target: null,
        type: types.CUSTOMER_NOT_UPDATED,
    });


/**
 * I create a customer page loaded event.
 *
 * :param customers: loaded customers
 * :param page: loaded customers page
 *
 * :returns: customer page loaded event
 */
function customerPageLoaded (
    customers: ICustomer[],
    page: number,
): Events.ICustomerPageLoaded
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_PAGE_LOADED,
        _target: null,
        customers,
        page,
        type: types.CUSTOMER_PAGE_LOADED,
    };
}


/**
 * I create a customer untagged event.
 *
 * :param id: customer id
 * :param customerTag: customer tag association id
 *
 * :returns: customer untagged event
 */
function customerUntagged (
    id: number,
    customerTag: number,
): Events.ICustomerUntagged
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_UNTAGGED,
        _target: id,
        customerTag,
        type: types.CUSTOMER_UNTAGGED,
    };
}


/**
 * I create an update succeeded event.
 *
 * :param address1: customer address1
 * :param address2: customer address2
 * :param agents: customer agents
 * :param city: customer city
 * :param code: customer code
 * :param company: customer company
 * :param contacts: customer contacts
 * :param document: customer document
 * :param email: customer email
 * :param id: customer id
 * :param name: customer name
 * :param description: customer description
 * :param organization: customer organization
 * :param state: customer state
 * :param zipcode: customer zipcode
 * :param tags: customer tags
 *
 * :returns: update succeeded event
 */
const customerUpdated: IEventCreator<Events.ICustomerUpdated> =
    (
        address1: string,
        address2: string,
        agents: ICustomerAgent[],
        city: string,
        code: string,
        company: number,
        contacts: IContact[],
        document: string,
        email: string,
        id: number,
        name: string,
        description: string,
        organization: string,
        state: string,
        zipcode: string,
        tags?: ICustomerTag[],
    ): Events.ICustomerUpdated => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_UPDATED,
        _target: id,
        address1,
        address2,
        agents,
        city,
        code,
        company,
        contacts,
        description,
        document,
        email,
        name,
        organization,
        state,
        tags,
        type: types.CUSTOMER_UPDATED,
        zipcode,
    });


/**
 * I create an updating customer event.
 *
 * :returns: updating event
 */
const customerUpdating: IEventCreator<Events.ICustomerUpdating> =
    (): Events.ICustomerUpdating => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_UPDATING,
        _target: null,
        type: types.CUSTOMER_UPDATING,
    });


/**
 * I create an event to reset errors.
 *
 * :returns: reset event
 */
const customerErrorReset: IEventCreator<Events.ICustomerErrorReset> =
    (): Events.ICustomerErrorReset => ({
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_ERROR_RESET,
        _target: null,
        type: types.CUSTOMER_ERROR_RESET,
    });


/**
 * I create an customer fetched event.
 *
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param agents: agents of the customer
 * :param city: city of the customer
 * :param code: code of the customer
 * :param company: company of the customer
 * :param contacts: contacts of the customer
 * :param description: description of the customer
 * :param document: document of the customer
 * :param email: email of the customer
 * :param id: id of the customer
 * :param name: name of the customer
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param tags: tags of the customer
 * :param zipcode: zipcode of the customer
 *
 * :returns customer fetched event
 */
function customerFetched (
    address1: string,
    address2: string,
    agents: ICustomerAgent[],
    city: string,
    code: string,
    company: number,
    contacts: IContact[],
    description: string,
    document: string,
    email: string,
    id: number,
    name: string,
    organization: string,
    state: string,
    tags: ICustomerTag[],
    zipcode: string,
): Events.ICustomerFetched
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_FETCHED,
        _target: id,
        address1,
        address2,
        agents,
        city,
        code,
        company,
        contacts,
        description,
        document,
        email,
        name,
        organization,
        state,
        tags,
        type: types.CUSTOMER_FETCHED,
        zipcode,
    };
}


/**
 * I create a customer tags filter updated event.
 *
 * :returns: customer tags filter updated loading event
 */
function customerTagsFilterUpdated (
    tags: number[],
): Events.ICustomerTagsFilterUpdated
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_TAGS_FILTER_UPDATED,
        _target: null,
        tags,
        type: types.CUSTOMER_TAGS_FILTER_UPDATED,
    };
}


/**
 * I create a customer whatsapp blocked event.
 *
 * :returns: customer whatsapp blocked event
 */
function customerWhatsappBlocked(): Events.ICustomerWhatsappBlocked
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_WHATSAPP_BLOCKED,
        _target: null,
        type: types.CUSTOMER_WHATSAPP_BLOCKED,
    };
}


/**
 * I create a customer whatsapp blocking event.
 *
 * :returns: customer whatsapp blocking event
 */
function customerWhatsappBlocking(): Events.ICustomerWhatsappBlocking
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_WHATSAPP_BLOCKING,
        _target: null,
        type: types.CUSTOMER_WHATSAPP_BLOCKING,
    };
}


/**
 * I create a customer whatsapp not blocked event.
 *
 * :param error: error code
 *
 * :returns: customer whatsapp not blocked event
 */
function customerWhatsappNotBlocked(
    error: string,
): Events.ICustomerWhatsappNotBlocked
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_WHATSAPP_NOT_BLOCKED,
        _target: null,
        error,
        type: types.CUSTOMER_WHATSAPP_NOT_BLOCKED,
    };
}


/**
 * I create a customer whatsapp not unblocked event.
 *
 * :param error: error code
 *
 * :returns: customer whatsapp not unblocked event
 */
function customerWhatsappNotUnblocked(
    error: string,
): Events.ICustomerWhatsappNotUnblocked
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_WHATSAPP_NOT_UNBLOCKED,
        _target: null,
        error,
        type: types.CUSTOMER_WHATSAPP_NOT_UNBLOCKED,
    };
}


/**
 * I create a customer whatsapp unblocked event.
 *
 * :returns: customer whatsapp unblocked event
 */
function customerWhatsappUnblocked(): Events.ICustomerWhatsappUnblocked
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_WHATSAPP_UNBLOCKED,
        _target: null,
        type: types.CUSTOMER_WHATSAPP_UNBLOCKED,
    };
}


/**
 * I create a customer whatsapp unblocking event.
 *
 * :returns: customer whatsapp unblocking event
 */
function customerWhatsappUnblocking(): Events.ICustomerWhatsappUnblocking
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: types.CUSTOMER_WHATSAPP_UNBLOCKING,
        _target: null,
        type: types.CUSTOMER_WHATSAPP_UNBLOCKING,
    };
}


/**
 * EXPORTS
 */
export {
    customerAdded,
    customerAllPagesLoaded,
    customerContactAlreadyExists,
    customerContactTypeAdded,
    customerContactTypesFetched,
    customerContactTypesFetching,
    customerContactTypesNotFetched,
    customerCreated,
    customerCreating,
    customerErrorReset,
    customerFetched,
    customerFocused,
    customerLoading,
    customerNotCreated,
    customerNotLoaded,
    customerNotRemoved,
    customerNotTagged,
    customerNotUntagged,
    customerNotUpdated,
    customerPageLoaded,
    customerRemoved,
    customerRemoving,
    customerSearchTermAdded,
    customerSiblingsDropped,
    customerSiblingsLoaded,
    customerSiblingsLoading,
    customerTagged,
    customerTagging,
    customerTagsFilterUpdated,
    customerUntagged,
    customerUpdated,
    customerUpdating,
    customerWhatsappBlocked,
    customerWhatsappBlocking,
    customerWhatsappNotBlocked,
    customerWhatsappNotUnblocked,
    customerWhatsappUnblocked,
    customerWhatsappUnblocking,
};
